import React, { useCallback, useEffect, useRef, useState } from 'react';
import close from '../../../assets/img/svg//table/close.svg';
import blueBullet from '../../../assets/img/svg/cash-register/blue-bullet.svg';
import animation from '../../../assets/animation/MZKc7dDskN.json';
import greenBullet from '../../../assets/img/svg//cash-register/green-bullet.svg';
import { DateFilter } from '../../ContentWrapper/Header/DatePicker/DatePicker';
import DateAndTime from '../../ContentWrapper/Header/DateAndTime';
import { useAppSelector } from '../../../hook/useAppSelector';
import Categories from './Categories';
import { Game, Name, Operator } from '../../../interface/response/CashRegisterDataResponse';
import backArrow from '../../../assets/img/svg/cash-register/back-arrow.svg';
import MoneyRow from './MoneyRow';
import { setOperatorModalOpen, setSelectedOperator } from '../../../store/slice/operator-details-slice';
import { useAppDispatch } from '../../../hook/useAppDispatch';
import { getMoneyClass } from '../../../util/formatCurrencySymbol';
import { stringToColor } from '../../../util/stringToColor';
import { CashRegisterMode } from '../../../enum/CashRegisterMode';
import { t } from 'i18next';
import { useOutsideFocus } from '../../../hook/useOutsideFocus';
import { SlideDirection } from '../../../enum/SlideDirection';
import Lottie from 'lottie-react';
import { numberFormatter } from '../../../util/numberFormatter';
import { ANIMATION_DURATION_MS } from '../../../constant/constants';

interface DetailsLayoutProps {
  title: string;
  startState: number | null;
  endState: number | null;
  mode?: CashRegisterMode.CASH_REGISTER_DETAILS | CashRegisterMode.OPERATOR_DETAILS;
  onClose: () => void;
  onSearch: (item: Name) => void;
  className?: string;
  aplicationGames: Game[];
  operators: Operator;
  loading?: boolean;
}

const DetailsLayout: React.FC<DetailsLayoutProps> = ({
  title,
  aplicationGames,
  startState,
  endState,
  mode = CashRegisterMode.CASH_REGISTER_DETAILS,
  onClose,
  onSearch,
  className,
  operators,
  loading = false,
}) => {
  const dispatch = useAppDispatch();

  const selectedLocation = useAppSelector((state) => state.cashRegisterData.selectedLocation);
  const selectedCCY = useAppSelector((state) => state.cashRegisterData.selectedCCY);
  const selectedOperator = useAppSelector((state) => state.operatorDetails.selectedOperator);
  const operatorDetails = useAppSelector((state) => state.operatorDetails);
  const format = useAppSelector((state) => state.configSlice.format);
  const ref = useRef<HTMLDivElement>(null);
  const [slideState, setSlideState] = useState<
    SlideDirection.IN | SlideDirection.OUT_LEFT | SlideDirection.OUT_RIGHT | SlideDirection.NONE
  >(SlideDirection.NONE);

  useEffect(() => {
    setSlideState(SlideDirection.IN);
  }, []);

  const handleReturn = () => {
    setSlideState(SlideDirection.OUT_RIGHT);

    setTimeout(() => {
      dispatch(setOperatorModalOpen(false));
      dispatch(setSelectedOperator(null));

      setSlideState(SlideDirection.IN);
    }, ANIMATION_DURATION_MS);
  };

  const handleClose = useCallback(() => {
    setSlideState(SlideDirection.OUT_RIGHT);

    setTimeout(() => {
      onClose();
    }, ANIMATION_DURATION_MS);
  }, [onClose]);

  useOutsideFocus(true, handleClose, ref);

  return (
    <div
      ref={ref}
      className={`details_container 
    ${slideState === SlideDirection.IN ? 'slide_in' : ''} 
    ${slideState === SlideDirection.OUT_LEFT ? 'slide_out' : ''} 
    ${slideState === SlideDirection.OUT_RIGHT ? 'slide_out_right' : ''} 
    ${className || ''}`}
    >
      <header className={mode === CashRegisterMode.CASH_REGISTER_DETAILS ? 'cash_header' : 'operator_header'}>
        <span className="details">{title}</span>

        {mode === CashRegisterMode.OPERATOR_DETAILS && (
          <span className="return" onClick={handleReturn}>
            <img src={backArrow} className="back_arrow" alt="back" />
            {t('cashRegister.table.details.return')}
          </span>
        )}

        <img src={close} alt="close" onClick={handleClose} />
      </header>

      <div className="info_container">
        <div className="location_container">
          <div className="location_info">
            <span className="title">{t('cashRegister.table.details.location')}:</span>
            <span className="selected_location">{selectedLocation}</span>
          </div>

          <div className="date_info">
            <div className="range_dropdown">
              <DateFilter onSearch={onSearch} />
            </div>
            <div className="current_date_and_time">
              {t('cashRegister.table.details.currentDateAndTime')}: <DateAndTime />
            </div>
          </div>
        </div>
        {mode === CashRegisterMode.OPERATOR_DETAILS && selectedOperator !== null && selectedOperator.UserName && (
          <div className="operator_info info">
            <span className="initials" style={{ backgroundColor: stringToColor(selectedOperator.UserName) }}>
              {selectedOperator.UserName.split(' ')
                .map((n) => n[0])
                .join('')}
            </span>
            <span className="operator_name name">{selectedOperator.UserName}</span>
          </div>
        )}

        <div className="balance_container">
          {loading ? (
            <span className="spinner">
              <Lottie
                animationData={animation}
                loop={true}
                autoPlay={true}
                style={{
                  width: '17px',
                  height: '17px',
                  transformOrigin: 'center',
                  transform: 'scale(3)',
                  overflow: 'hidden',
                }}
              ></Lottie>
            </span>
          ) : (
            <>
              <div className="start_state state">
                <div className="title_container">
                  <img src={blueBullet} alt="blue" className="bullet blue" />
                  <span className="title">{t('cashRegister.table.details.startState')}</span>
                </div>

                <div className={`balance_container ${getMoneyClass(selectedCCY)}`}>
                  <span className="balance">{startState !== null && numberFormatter(Number(startState), format)}</span>
                  <span className="currency">{selectedCCY}</span>
                </div>
              </div>

              <div className="end_state state">
                <div className="title_container">
                  <img src={greenBullet} alt="green" className="bullet green" />
                  <span className="title">{t('cashRegister.table.details.endState')}</span>
                </div>

                <div className={`balance_container ${getMoneyClass(selectedCCY)}`}>
                  <span className="balance">{endState !== null && numberFormatter(Number(endState), format)}</span>
                  <span className="currency">{selectedCCY}</span>
                </div>
              </div>
            </>
          )}
        </div>
        {mode === CashRegisterMode.OPERATOR_DETAILS && (
          <div className="container operator_status_container">
            <div className="item">
              <span className="title">{t('cashRegister.table.details.payInAndPayOut')}</span>
              <div className="values">
                <MoneyRow
                  label={t('cashRegister.table.details.payIn')}
                  amount={operatorDetails.data?.PayInPayOut?.PayIn}
                />
                <MoneyRow
                  label={t('cashRegister.table.details.payOut')}
                  amount={operatorDetails.data?.PayInPayOut?.PayOut}
                />
                <MoneyRow
                  label={t('cashRegister.table.details.balance')}
                  amount={operatorDetails.data?.PayInPayOut?.Balance}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="category_container">
        <Categories aplicationGames={aplicationGames} mode={mode} operators={operators} />
      </div>
    </div>
  );
};

export default DetailsLayout;
